import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAddFine } from "../api/fines-api";
import { queryClient } from "../api/queryClient";
import { useLogeMembers } from "../api/user-api";
import AvatarCircle from "../components/Avatar";
import { Icon } from "../components/dss/Icon";
import { AdminHeader, Field } from "../components/dss/Primitives";
import { useSupabaseAuth } from "../supabase/SupabaseContext";

const PRESETS = [25, 50, 100];

const STANDARD_BODETAKST = 25;

const SUGGESTIONS: { label: string; takster: number }[] = [
  { label: "Afmeldt/udeblevet – inden 1 måned", takster: 1 },
  { label: "Afmeldt/udeblevet – inden 1 uge", takster: 2 },
  { label: "Afmeldt/udeblevet – herefter", takster: 4 },
  { label: "For sent – efter 1 svensk kvarter", takster: 1 },
  { label: "For sent – efter 2 svenske kvarter", takster: 2 },
  { label: "For sent – efter 4 svenske kvarter", takster: 3 },
  { label: "For sent – efter en dansk time", takster: 4 },
  { label: "Opkast til arrangement", takster: 2 },
  { label: "Cockblocking", takster: 4 },
  { label: "Manglende dresscode", takster: 2 },
  { label: "Drak sidste øl uden at skaffe nye", takster: 1 },
  { label: "Værten aflyste arrangementet", takster: 4 },
  { label: "Værten sendte ikke info ud i tide", takster: 2 },
  { label: "Dårlig stemning", takster: 1 },
  { label: "Dårlig stemning (2. gang)", takster: 2 },
  { label: "Manglende rettidig betaling", takster: 1 },
  { label: "Svigt af ministeransvar", takster: 2 },
  { label: "Ødelagde arrangementet", takster: 8 },
  { label: "Sendte ikke datoer rettidigt", takster: 1 },
];

const AddFine = () => {
  const { data: members } = useLogeMembers();
  const { member: me } = useSupabaseAuth();
  const { mutateAsync: addFine, isLoading } = useAddFine();
  const navigate = useNavigate();

  const [payerId, setPayerId] = useState<number | null>(null);
  const [amount, setAmount] = useState(25);
  const [reason, setReason] = useState("");
  const [witnessIds, setWitnessIds] = useState<number[]>([]);

  const payer = members?.find((m) => m.id === payerId);

  const toggleWitness = (id: number) => {
    setWitnessIds((prev) => (prev.includes(id) ? prev.filter((w) => w !== id) : [...prev, id]));
  };

  const onSubmit = async () => {
    if (!payerId || !me || !reason || amount <= 0) return;
    await addFine({
      logeMemberId: payerId,
      amountKr: amount,
      reason,
      issuedBy: me.id,
      witnessIds,
    });
    queryClient.invalidateQueries(["fines"]);
    navigate("/penalties");
  };

  return (
    <div className="mx-auto max-w-md bg-paper pt-safe h-app flex flex-col overflow-hidden">
      <AdminHeader
        back="Annullér"
        onBack={() => navigate("/penalties")}
        title={<>Ny bøde<span className="text-clay">.</span></>}
        sub={me ? `UDSTEDES AF ${me.name.toUpperCase()}` : undefined}
      />

      <div className="px-7 pt-6 flex flex-col gap-6 flex-1 overflow-y-auto min-h-0">
        <Field label="Hvem skal betale?">
          <div className="grid grid-cols-4 gap-3">
            {members?.map((m) => (
              <AvatarCircle
                key={m.id}
                userId={m.id}
                size="md"
                selected={m.id === payerId}
                dim={payerId !== null && m.id !== payerId}
                onClick={() => setPayerId(m.id)}
              />
            ))}
          </div>
        </Field>

        <Field label="Beløb">
          <div className="flex items-baseline gap-0 border-b-2 border-ink pb-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={amount === 0 ? "" : String(amount)}
              onChange={(e) => setAmount(Number(e.target.value.replace(/\D/g, "")) || 0)}
              placeholder="0"
              className="w-32 bg-transparent font-display font-extrabold text-[56px] leading-[0.85] tracking-tight tabular-nums focus:outline-none"
            />
            <div className="font-mono text-sm font-semibold text-ink3 ml-2">KR.</div>
          </div>
          <div className="flex gap-1.5 mt-3">
            {PRESETS.map((p) => {
              const active = p === amount;
              return (
                <button
                  key={p}
                  onClick={() => setAmount(p)}
                  className={`flex-1 py-2.5 text-center rounded-lg border font-display text-sm font-bold tracking-tight tabular-nums ${
                    active ? "bg-ink border-ink text-paper" : "border-line text-ink"
                  }`}
                >
                  {p}
                  <span className="font-mono text-[10px] font-medium ml-0.5 opacity-70">KR</span>
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Begrundelse">
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Hvad skete der?"
            className="w-full border-b-2 border-ink pb-2 text-base font-medium bg-transparent focus:outline-none"
          />
          <div className="flex flex-wrap gap-1.5 mt-3">
            {SUGGESTIONS.map((s) => (
              <button
                key={s.label}
                onClick={() => {
                  setReason(s.label);
                  setAmount(s.takster * STANDARD_BODETAKST);
                }}
                className="py-1.5 px-2.5 rounded-lg border border-line bg-paper2 font-mono text-[10.5px] tracking-wide text-ink2"
              >
                {s.label}
                <span className="opacity-60"> · {s.takster * STANDARD_BODETAKST} KR</span>
              </button>
            ))}
          </div>
        </Field>

        <Field label="Vidner · valgfrit">
          <div className="flex gap-1.5 flex-wrap">
            {members
              ?.filter((m) => m.id !== payerId)
              .map((m) => {
                const on = witnessIds.includes(m.id);
                return (
                  <button
                    key={m.id}
                    onClick={() => toggleWitness(m.id)}
                    className={`flex items-center gap-1.5 py-1 pl-1 pr-2.5 rounded-lg border ${
                      on ? "bg-ink border-ink text-paper" : "bg-transparent border-line text-ink2"
                    }`}
                  >
                    <span className="text-sm">{m.emoji}</span>
                    <span className="font-mono text-[10.5px] tracking-wide font-medium uppercase">
                      {m.name}
                    </span>
                  </button>
                );
              })}
          </div>
        </Field>
      </div>

      <div className="px-7 pb-5 pt-3">
        <button
          disabled={!payerId || !reason || amount <= 0 || isLoading}
          onClick={onSubmit}
          className="w-full py-3.5 px-4 rounded-lg bg-clay text-paper font-display text-sm font-bold tracking-wide uppercase flex items-center justify-between gap-3 disabled:opacity-40"
        >
          <span className="flex items-center gap-2 min-w-0">
            <Icon name="circle" size={7} className="flex-shrink-0" />
            <span className="truncate">Udsted bøde{payer ? ` til ${payer.name}` : ""}</span>
          </span>
          <span className="font-mono text-[11px] opacity-75 flex items-center gap-1.5 flex-shrink-0">
            {amount} KR
            <Icon name="arrow-right" size={13} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default AddFine;
